import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Building, MaiaAccessConfig } from '../interface/buildings.interface';
import { CovertureZoneResponse } from '../interface/services.interface';
import { unitsResponse } from '../interface/units.interface';
import { DataService } from './data.service';
import { CreateDoor } from '../interface/door.interface';
import { ApiDetailResponse } from '../interface/data.interface';
import { unwrapNewApiEnvelope } from 'src/app/pages/areas/new-area-v2/services/new-api-envelope.util';
import {
  BuildingMacroCommunityResponse,
  UpdateBuildingMacroCommunityRequest,
  UpdateBuildingMacroCommunityResponse,
} from './building-macro-community.models';

/** @deprecated Prefer UpdateBuildingMacroCommunityRequest — kept for callers of updateConfigBuildingMacro. */
export interface UpdateBuildingMacro {
  enable: boolean;
  buildings: string[];
}

@Injectable()
export class BuildingService extends DataService {
  currentBuilding: Building;

  constructor(private http: HttpClient, protected injector: Injector, protected store: Store<any>) {
    super(injector, store);
    this.currentBuilding = this.getInitialBuilding();
  }


  listBuildings(page, limit, search, find): any {
    let params: string = search ? `&${search}` : '';
    if (!params.includes("status")) params += "&status=active";
    // VDS-3529: simple search used legacy `search=`; migrated to `name=` (same as advance-search)
    // so the new API returns matching buildings consistently.
    const safeFind = typeof find === 'string' ? find.trim() : '';
    find = safeFind ? `&name=${encodeURIComponent(safeFind)}` : '';

    return this.http
      .get(`${environment.newApiUrl}/api/super/v1/buildings?page=${page}&rowsPerPage=${limit}${params}${find}`)
      .toPromise()
      .then((res) => {
        return res || [];
      })
      .catch((err) => {
        console.log('errr: ', err);
        Promise.reject(err);
      });
  }

  getMunilyServices() {
    return this.http.get(`${environment.apiUrl}/super/v1/munily-trucks`);
  }

  generateQrForBuilding(buildingId: string, imageBase64) {
    return this.http.post(`${environment.apiUrl}/super/v1/buildings/${buildingId}/generate-qr`, imageBase64);
  }

  getBuildings(params?: HttpParams) {
    return this.http.get(`${environment.apiUrl}/super/v1/buildings/list`, { params });
  }

  getUnitsByBuilding(buildingId: any) {
    return this.http.get(`${environment.apiUrl}/super/v1/units/${buildingId}/all`);
  }

  getUnitsByBuildingWithOwners(buildingId: any) {
    return this.http.get(`${environment.apiUrl}/super/v1/units/${buildingId}/allWithOwners`);
  }

  newStoreBuilding(data) {
    if (data.building.operationZone) {
      data.building.operationZone = data.building.operationZone.id;
    }
    return this.http.post(`${environment.apiUrl}/super/v1/buildings`, data);
  }

  newUpdateBuilding(data, id) {
    return this.http.put(`${environment.apiUrl}/super/v1/buildings/${id}`, data);
  }

  newUpdateBuildingConfig(data, id) {
    return this.http.put(`${environment.apiUrl}/super/v1/buildings/${id}/config`, data);
  }

  updateOperationsConfig(data, id) {
    return this.http.put(`${environment.apiUrl}/super/v1/buildings/${id}/config-ops`, data);
  }

  createSecurityCompany(data) {
    return this.http.post(`${environment.apiUrl}/super/v1/security-companies`, data);
  }

  editSecurityCompany(id: string, data) {
    return this.http.put(`${environment.apiUrl}/super/v1/security-companies/${id}`, data);
  }

  removeSecurityCompany(id: string) {
    return this.http.delete(`${environment.apiUrl}/super/v1/security-companies/${id}`);
  }

  updateBuildingConfig(data, id) {
    return this.http.put(`${environment.apiUrl}/admin/v1/buildings/${id}/config`, data);
  }

  getOneBuildingById(id) {
    return this.http.get(`${environment.apiUrl}/super/v1/buildings/${id}`);
  }

  /**
   * Nest L1: macrocommunity hierarchy (multipadre-safe).
   * Replaces legacy `GET …/super/v1/buildings/admin-macro/:id`.
   */
  getBuildingMacroConfig(id: string): Observable<BuildingMacroCommunityResponse> {
    const url = `${environment.newApiBackend}/api/v2/buildings/super-admin/${id}/macro-community`;
    return this.http
      .get<ApiDetailResponse<BuildingMacroCommunityResponse>>(url)
      .pipe(map((res) => unwrapNewApiEnvelope<BuildingMacroCommunityResponse>(res)));
  }

  /**
   * Nest L1: enable/update children of this father (additive associate / remove).
   * Replaces legacy `PUT …/super/v1/buildings/admin-macro/update/:id`.
   */
  updateConfigBuildingMacro(
    id: string,
    data: UpdateBuildingMacro | UpdateBuildingMacroCommunityRequest,
  ): Observable<UpdateBuildingMacroCommunityResponse> {
    const url = `${environment.newApiBackend}/api/v2/buildings/super-admin/${id}/macro-community`;
    return this.http
      .put<ApiDetailResponse<UpdateBuildingMacroCommunityResponse>>(url, data)
      .pipe(map((res) => unwrapNewApiEnvelope<UpdateBuildingMacroCommunityResponse>(res)));
  }

  storeBuilding(data) {
    return this.http
      .post(`${environment.apiUrl}/building`, data)
      .toPromise()
      .then((res) => {
        return res || [];
      })
      .catch((err) => {
        console.log('errr: ', err);
        return Promise.reject(err);
      });
  }

  viewBuilding(id) {
    return this.http
      .get(`${environment.apiUrl}/building/${id}?wUsers=true&wApart=true&roles=admin`)
      .toPromise()
      .then((res) => {
        return res || [];
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  updateBuilding(data) {
    return this.http
      .put(`${environment.apiUrl}/building/${data.building.id}`, data)
      .toPromise()
      .then((res) => {
        return res || [];
      })
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  updateStatusBuilding(id: string, status: string): Observable<Building> {
    return this.http.put<Building>(`${environment.apiUrl}/super/v1/buildings/services/status/${id}`, { status });
  }

  updateBuildingPlan(id, plan) {
    return this.http
      .put(`${environment.apiUrl}/building/${id}/update-plan`, { plan })
      .toPromise()
      .then((res) => {
        return res || [];
      })
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  fetchComments(buildingId) {
    return this.http
      .get(`${environment.apiUrl}/comment?building=${buildingId}`)
      .toPromise()
      .then((res) => {
        return res || [];
      })
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  submitComment(data) {
    return this.http
      .post(`${environment.apiUrl}/comment`, data)
      .toPromise()
      .then((res) => {
        return res || [];
      })
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  getRegions() {
    return this.http.get(`${environment.apiUrl}/regions`);
  }

  donwloadInfoInstall(id) {
    window.open(`${environment.apiUrl}/building/${id}/generateInfoFirstLogin`, '_blank');
  }

  getEmailUserUnit(unitId: string) {
    return this.http.put(`${environment.apiUrl}/admin/v1/units/users/${unitId}`, {});
  }

  getCovertureZones() {
    return this.http.get(`${environment.apiUrl}/super/v1/munily-trucks/region`) as Observable<CovertureZoneResponse>;
  }

  unitsMapper(units: unitsResponse[]) {
    return units.map((unit) => {
      return {
        id: unit.unitId,
        number: unit.unitNumber,
      };
    });
  }

  updateExperiments(buildingId: string, experiments) {
    return this.http.post(`${environment.apiUrl}/super/v1/buildings/${buildingId}/experiments`, { experiments });
  }

  private getInitialBuilding(): Building {
    return this.getBuilding();
  }

  getInformationDevices(userId: string, params: HttpParams) {
    return this.http.get(`${environment.apiUrl}/super/v1/buildings/${userId}/devices`, { params });
  }

  updateConfigQpassBuilding(buildingId: string) {
    return this.http.get(`${environment.apiUrl}/super/v1/buildings/${buildingId}/config/qpass`);
  }

  updateConfigAccessDoorBuilding(buildingId: string) {
    return this.http.get(`${environment.apiUrl}/super/v1/buildings/${buildingId}/config/access-door`);
  }

  updateConfigQpassVehicleBuilding(buildingId: string) {
    return this.http.get(`${environment.apiUrl}/super/v1/buildings/${buildingId}/config/qpass/vehicle`);
  }

  updateConfigQpassExtendTimeBuilding(buildingId: string, payload: { enable: boolean, extendTime: number }) {
    return this.http.patch(`${environment.apiUrl}/super/v1/buildings/${buildingId}/config/qpass/extend-time`, payload);
  }

  getDoors(params: HttpParams) {
    return this.http.get(`${environment.apiUrl}/super/v1/door`, { params });
  }

  createDoor(payload: CreateDoor) {
    return this.http.post(`${environment.apiUrl}/super/v1/door`, payload);
  }

  updateDoor(doorId: string, payload: CreateDoor) {
    return this.http.put(`${environment.apiUrl}/super/v1/door/${doorId}`, payload);
  }

  getDoorsBuilding(buildingId: string) {
    return this.http.get(`${environment.apiUrl}/admin/v1/door/${buildingId}`);
  }

  getVehicleDataStepOptional(buildingId: string) {
    return this.http.get(`${environment.apiUrl}/super/v1/buildings/${buildingId}/step/vehicle`);
  }

  getBuildingConfigQpassVehicle(buildingId: string) {
    return this.http.get(`${environment.apiUrl}/super/v1/buildings/config/${buildingId}/qpass/vehicle`);
  }

  getActiveMaiaiFaceRecognition() {
    return this.http.get(`${environment.apiUrl}/admin/v1/buildings/config/maia/face-recognition`);
  }

  getBuildingsWithMaiaActive(): Observable<ApiDetailResponse<{ id: string, name: string }[]>> {
    return this.http.get<ApiDetailResponse<{ id: string, name: string }[]>>(`${environment.newApiBackend}/api/super-admin/building/config/maia/active`);
  }

  removeBuildingOfDoor(buildingId: string, doorId: string): Observable<ApiDetailResponse<{ id: string }>> {
    return this.http.delete<ApiDetailResponse<{ id: string }>>(`${environment.newApiBackend}/api/super-admin/building/${buildingId}/config/access-control/access-door/${doorId}`);
  }

  getConfigurationMaia(buildingId: string): Observable<ApiDetailResponse<MaiaAccessConfig>> {
    return this.http.get<ApiDetailResponse<MaiaAccessConfig>>(`${environment.newApiBackend}/api/super-admin/building/${buildingId}/config/maia`);
  }

  updateConfigurationMaiaFaceRecognition(buildingId: string, payload: { enabled: boolean }): Observable<ApiDetailResponse<{ id: string }>> {
    return this.http.patch<ApiDetailResponse<{ id: string }>>(`${environment.newApiBackend}/api/super-admin/building/${buildingId}/config/access-control/maia/face-recognition`, payload);
  }

  updateConfigurationMaiaPin(buildingId: string, payload: { enabled: boolean }): Observable<ApiDetailResponse<{ id: string }>> {
    return this.http.patch<ApiDetailResponse<{ id: string }>>(`${environment.newApiBackend}/api/super-admin/building/${buildingId}/config/access-control/maia/pin`, payload);
  }
 
  updateBuildingDatabaseCertificationContent(enable: boolean) {
    return this.http.patch(`${environment.apiUrl}/admin/v1/buildings/certification-content`, { enable })
  }
}
